import { formatCurrency, formatDate } from '@if/core/utils'
import EventIcon from '@mui/icons-material/Event'
import { IconButton, Tooltip } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { useMonolithBaseUrl } from '@src/services/monolith-hook'
import { InteracWire } from '@src/types/interac'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const useRecordedInteracWireColumns = (): GridColDef<InteracWire>[] => {
  const { t } = useTranslation()
  const monolithBaseUrl = useMonolithBaseUrl()

  return useMemo<GridColDef<InteracWire>[]>(
    () => [
      {
        field: 'receivedOn',
        headerName: t('common.date'),
        sortable: false,
        minWidth: 130,
        disableColumnMenu: true,
        valueFormatter: (receivedOn: Date) => {
          if (receivedOn.toString() === '0001-01-01T00:00:00') return ''
          return formatDate(receivedOn)
        },
      },
      {
        field: 'amount',
        headerName: t('interacWire.amount'),
        sortable: false,
        align: 'right',
        disableColumnMenu: true,
        valueFormatter: formatCurrency,
      },
      {
        field: 'sentBy',
        headerName: t('interacWire.from'),
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => `${row.sentBy} (${row.senderEmail})`,
      },
      {
        field: 'message',
        headerName: t('interacWire.message'),
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'linkedBy',
        headerName: t('interacHistory.linkedBy'),
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'linkedOn',
        headerName: t('interacHistory.linkedOn'),
        minWidth: 130,
        sortable: false,
        disableColumnMenu: true,
        valueFormatter: (linkedOn: Date) => {
          if (!linkedOn) return ''
          return formatDate(linkedOn)
        },
      },
      {
        field: 'linkedLoanId',
        headerName: t('interacHistory.loanInfo'),
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => {
          if (!row.linkedLoanId) return ''
          const program = row.linkedProgram ?? ''
          return `${program} #${row.linkedLoanId}`
        },
      },
      {
        field: 'clientName',
        headerName: t('interacHistory.clientName'),
        minWidth: 180,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => {
          if (!row.linkedLoanId || !row.possibleMatches) return '—'
          const match = row.possibleMatches.find((m) => m.loanId === row.linkedLoanId)
          if (!match) return '—'
          return `${match.applicantFirstName} ${match.applicantLastName}`
        },
      },
      {
        field: 'calendar',
        headerName: '',
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        resizable: false,
        width: 60,
        renderCell: ({ row }) => {
          if (!row.linkedLoanId) return null
          return (
            <Tooltip title={t('interacHistory.paymentCalendar')}>
              <IconButton
                href={`${monolithBaseUrl}/admin/customer/${row.linkedLoanId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <EventIcon />
              </IconButton>
            </Tooltip>
          )
        },
      },
    ],
    [monolithBaseUrl, t],
  )
}
